import { type IUser } from '../../models/User'

// Returns unique userIds from a list, optionally excluding some
const getUserIds = (students: IUser[], exclude: string[] = []): string[] =>
  students
    .map(s => s?.userId)
    .filter((id, idx, arr) => id && arr.indexOf(id) === idx && !exclude.includes(id))

// Filters out students whose userId is in excludeIds
const filterOutByIds = (students: IUser[], excludeIds: string[]): IUser[] =>
  students.filter(s => !excludeIds.includes(s?.userId))

// Filters enrolled students by search and not in unenrolled
export const filterStudentsBySearch = (
  studentsToBeFiltered: IUser[],
  newlyOppositeList: IUser[],
  search: string
): IUser[] => {
  const lowered = search.toLowerCase().trim()
  const unenrolledIds = getUserIds(newlyOppositeList)

  return studentsToBeFiltered.filter(s =>
    getSearchableStudentText(s).includes(lowered) &&
    !unenrolledIds.includes(s?.userId)
  )
}

const getSearchableStudentText = (student: IUser): string => [
  student?.name,
  student?.firstName,
  student?.lastName,
  [student?.firstName, student?.lastName].filter(Boolean).join(' '),
  student?.email
]
  .filter((value): value is string => typeof value === 'string')
  .join(' ')
  .toLowerCase()

// Sorts search results into enrolled/unenrolled
export const sortSearchedStudentsByEnrollment = (
  searchResults: IUser[],
  enrollmentResults: IUser[],
  enrolledIds: string[],
  search: string,
  newlyUnenrolled: IUser[],
  newlyEnrolled: IUser[]
): { enrolled: IUser[], unenrolled: IUser[] } => {
  const enrolledStudents = [...filterStudentsBySearch(enrollmentResults, newlyUnenrolled, search)]
  const newlyEnrolledStudents = [...filterStudentsBySearch(newlyEnrolled, newlyUnenrolled, search)]
  const newlyUnenrolledStudents = [...filterStudentsBySearch(newlyUnenrolled, newlyEnrolled, search)]
  const enrolledSet = new Set([...enrolledStudents, ...newlyEnrolledStudents].map(s => s.userId))
  const result = { enrolled: [...enrolledStudents, ...newlyEnrolledStudents], unenrolled: [...newlyUnenrolledStudents] as IUser[] }
  for (const s of searchResults) {
    const sIsEnrolled = enrollmentResults.some(e => e.userId === s.userId) || newlyEnrolled.some(e => e.userId === s.userId)
    if (!sIsEnrolled) result.unenrolled.push(s)
    else if (enrolledIds.includes(s.userId) && !enrolledSet.has(s.userId)) {
      result.enrolled.push(s)
      enrolledSet.add(s.userId)
    }
  }
  return result
}

// Updates enrolled/unenrolled lists based on search and results
export const updateEnrollmentLists = (
  search: string,
  enrollmentResults: IUser[],
  newlyEnrolled: IUser[] | undefined,
  newlyUnenrolled: IUser[] | undefined,
  searchResults: IUser[] | undefined,
  setEnrolled: React.Dispatch<React.SetStateAction<IUser[]>>,
  setUnenrolled: React.Dispatch<React.SetStateAction<IUser[]>>
): void => {
  if (!search.trim()) {
    const excludeIds = getUserIds(newlyUnenrolled ?? [])
    setEnrolled(filterOutByIds([...enrollmentResults, ...(newlyEnrolled ?? [])], excludeIds))
    return
  }
  if (searchResults) {
    const excludeIds = getUserIds(newlyUnenrolled ?? [])
    const enrolledIds = getUserIds([...enrollmentResults, ...(newlyEnrolled ?? [])], excludeIds)
    const sorted = sortSearchedStudentsByEnrollment(
      searchResults,
      enrollmentResults,
      enrolledIds,
      search,
      newlyUnenrolled ?? [],
      newlyEnrolled ?? []
    )
    setUnenrolled(sorted.unenrolled)
    setEnrolled(sorted.enrolled)
  }
}
